/**
 * SceneBackground — attach a full-screen background image to a Phaser
 * scene, scaled to cover the visible area at depth -100.
 *
 * Used by the SDK shell scenes (MainMenu, Lobby, About, Settings,
 * GameOver) when the per-game `theme.backgrounds[<sceneKey>]` is set
 * via `bootGame({ theme: ... })`. Games that don't declare a theme see
 * the existing flat-color background — this helper is a no-op for them.
 *
 * The image is added at depth -100 so every other game object (UI text,
 * buttons, balloons, hand overlays) renders on top. We use Phaser's
 * scale-mode "envelope" math: pick the larger of (W/srcW, H/srcH) so
 * the background covers the screen even when its aspect ratio differs
 * from the scene viewport.
 *
 * `fromRegistry()` also sets the `<body>` CSS background to the same
 * image via `PageBackground`, so on desktop browsers (where the canvas
 * is letterboxed in FIT scale mode) the image still fills the entire
 * window — while the buttons inside the canvas stay at their design
 * positions.
 */
import { PageBackground } from '../platform/PageBackground.js';

export class SceneBackground {
  /**
   * @param {Phaser.Scene} scene
   * @param {string|undefined|null} key  Phaser texture cache key
   * @returns {Phaser.GameObjects.Image|null}
   */
  static attach(scene, key) {
    if (!scene || !key) return null;
    if (!scene.textures || !scene.textures.exists(key)) return null;

    const W = scene.scale.width;
    const H = scene.scale.height;
    const img = scene.add.image(W / 2, H / 2, key);
    img.setDepth(-100);
    img.setScrollFactor(0);

    // Cover-scale: pick whichever ratio fills the larger axis.
    const srcW = img.width || 1;
    const srcH = img.height || 1;
    const scale = Math.max(W / srcW, H / srcH);
    img.setScale(scale);

    return img;
  }

  /**
   * Convenience: read theme.backgrounds[sceneKey] from the registry,
   * attach it inside the scene, and mirror it onto the page body so
   * the full browser window is covered (handy on desktop where the
   * canvas is letterboxed).
   */
  static fromRegistry(scene) {
    const theme = scene.registry && scene.registry.get('theme');
    if (!theme || !theme.backgrounds) return null;
    const key = theme.backgrounds[scene.scene.key];
    const img = SceneBackground.attach(scene, key);
    // Mirror the same image onto the document body for full-window cover.
    if (key) PageBackground.set({ scene, key });
    return img;
  }
}
